import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { connect } from 'react-redux';
import {
  ErrorStatus, isCreating, isError, isSuccess, ResourcesItem,
} from 'redux-and-the-rest';
import {
  generatePath, Link, matchPath, withRouter,
} from 'react-router-dom';

import Header from '../shared/Header';
import TextInput from '../shared/TextInput';
import { UserModel } from '../../models/userModel';
import { clearUser, createUserSession, getUser } from '../../redux/reducers/userReducer';
import { ApplicationState } from '../../redux/types';
import { isEducator, isRegisteredUser } from '../../utils/UserUtils';
import { ModalRoutes, Routes, USE_MODAL_QUERY_PARAM } from '../../routes';
import NewSessionHandler from '../new-session-handler/NewSessionHandler';
import Env from '../../utils/env';

const { TEACHER_PORTAL } = Env;

const componentText = !TEACHER_PORTAL ? {
  username: {
    label: 'Email',
    error: {
      required: 'Your email address is required',
    },
  },
  password: {
    label: 'Password',
    error: {
      required: 'Your password is required',
    },
  },
} : {
  username: {
    label: 'Enter your school code',
    error: {
      required: 'Please enter your school code!',
    },
  },
  password: {
    label: 'Enter your passcode',
    error: {
      required: 'Your school passcode is required!',
    },
  },
};

interface LoginFormFields {
  email: string
  password: string
}

interface Props {
  onSubmit(email: string, password: string): void;
  user: ResourcesItem<UserModel>;
  location: {
    pathname:string;
    search: string;
  }
}

const LogInPanel = ({ onSubmit, user, location }: Props) => {
  const {
    register, handleSubmit, formState: { errors },
  } = useForm<LoginFormFields>();

  /**
   * if the User is returning from the reset password flow, we display a message
   * that functions as both a success message, and a prompt to use the new credentials.
   */
  const hasNewCredentials = !!matchPath(location.pathname.concat(location.search), {
    path: Routes.Session.NewCredentials,
    exact: true,
    strict: false,
  });

  /**
   * the API error in this case can only be generic invalid credentials message, which is
   * displayed at form level, rather than field level
   */
  const [formError, setFormError] = useState<ErrorStatus>();
  const isCreatingUserSession = isCreating(user);

  const submit = handleSubmit(({ email, password }) => {
    // We need to clear the resources associated with any Guest account when logging in.
    clearUser();

    setFormError(undefined);
    onSubmit(email, password);
  });

  useEffect(() => {
    if (isError(user)) {
      const { status: { errors: remoteErrors } } = user;
      /**
       * The remoteErrors in this case is an array of 1 item
       * https://adventure-bible-api-edge.dubitcloud.com/pages/api_docs/index.html#log-a-user-in
       */
      const remoteError = remoteErrors && remoteErrors[0].originalError?.detail;
      setFormError(remoteError);
    }
  }, [user.status]);

  // A Registered user check is required as we may have successfully created as a Guest user.
  if (isSuccess(user) && isRegisteredUser(user)) {
    // eslint-disable-next-line
    const redirectTo = TEACHER_PORTAL ?
      ModalRoutes.Island.ManageIslands.concat(`?${USE_MODAL_QUERY_PARAM}`)
      : isEducator(user) ? Routes.Account.Islands.Index
        : ModalRoutes.Island.ManageIslands.concat(`?${USE_MODAL_QUERY_PARAM}`);

    return <NewSessionHandler redirectTo={redirectTo} />;
  }

  const classNames = [
    'panel',
    'box',
  ];

  return (
    <div className={classNames.join(' ')}>
      <form className="form" onSubmit={submit}>
        <div className="form-section">
          {!TEACHER_PORTAL && (
            <Header variant="h2" title="Log in to account" />
          )}

          {hasNewCredentials && (
            <p className="message">
              You have successfully changed your password, you can now login
            </p>
          )}

          {TEACHER_PORTAL && (
            <p className="message">
              Please ask your teacher for the following codes:
            </p>
          )}

          <div className="form-group">
            <TextInput
              require
              label={componentText.username.label}
              useFormRegisterReturn={register('email', { required: componentText.username.error.required })}
              error={errors.email?.message}
            />
          </div>

          <div className="form-group">
            <TextInput
              require
              type={TEACHER_PORTAL ? 'text' : 'password'}
              label={componentText.password.label}
              useFormRegisterReturn={register('password', { required: componentText.password.error.required })}
              error={errors.password?.message}
            />
          </div>
        </div>

        {formError && (
          <div className="form-section form-message error">
            {formError}
          </div>
        )}

        <div className="form-section form-actions">
          <button className="button primary" type="submit" disabled={isCreatingUserSession}>
            Log In
          </button>
        </div>

        {!TEACHER_PORTAL && (
          <div className="form-section form-help">
            {/**
             * Pass through any search params passed to Login to the Register screen, such that the
             * redirect is carried through the new Session creation either way.
             * */
            }
            <Link to={generatePath(`${Routes.User.New}${location.search}`)} title="Create Account" className="link">
              Create Account
            </Link>

            <Link to={Routes.PasswordReset} title="Forgot Password" className="link">
              Forgot Password
            </Link>
          </div>
        )}
      </form>
    </div>
  );
};

const mapReduxStateToProps = ({ users }: ApplicationState) => ({
  onSubmit: (email: string, password: string) => {
    if (!TEACHER_PORTAL) {
      createUserSession({
        email, password, type: 'user', include: ['schools'],
      });
    } else {
      createUserSession({
        schoolCode: email, schoolPasscode: password, type: 'user', include: ['schools'],
      });
    }
  },
  user: getUser(users),
});

export default withRouter(connect(mapReduxStateToProps)(LogInPanel));
